import { type FC, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
  RiCheckboxCircleFill,
  RiCloseLine,
  RiErrorWarningFill,
  RiFileTextLine,
  RiRefreshLine,
  RiUploadCloud2Line,
} from 'react-icons/ri';
import { _notNil } from '@/littledash';
import { formatBytes } from '@/helpers';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useFileDropzone } from '@/support/Hooks/dropzone/useFileDropzone';
import type { UploadFieldProps } from '../VerticalStepForm.model';
import { fileNameFromUrl, validateFile } from './UploadField.utils';
import styles from './UploadField.module.scss';

interface PickedFile {
  name: string;
  size: number;
}

interface FieldError {
  kind: 'validation' | 'upload';
  message: string;
}

/**
 * Shared chrome for the file-state cards: document icon + name + a meta line +
 * a trailing dismiss button. The uploading/success/rejected cards below layer
 * their own colour, meta content, and testids on top of this.
 */
const FileCardShell: FC<{
  className?: string;
  rejected?: boolean;
  alert?: boolean;
  name: string;
  meta: ReactNode;
  dismissLabel: string;
  onDismiss: () => void;
  cardTestId?: string;
  filenameTestId?: string;
  dismissTestId?: string;
  children?: ReactNode;
}> = ({
  className = '',
  rejected = false,
  alert = false,
  name,
  meta,
  dismissLabel,
  onDismiss,
  cardTestId,
  filenameTestId,
  dismissTestId,
  children,
}) => (
  <div className={`${styles.fileCard} ${className}`} data-testid={cardTestId} role={alert ? 'alert' : undefined}>
    <div className={styles.fileRow}>
      <span className={`${styles.fileIcon} ${rejected ? styles.fileIconRejected : ''}`}>
        <RiFileTextLine size={20} />
      </span>
      <div className={styles.fileInfo}>
        <span className={styles.fileName} data-testid={filenameTestId}>
          {name}
        </span>
        <span className={styles.fileMeta}>{meta}</span>
      </div>
      <button
        type="button"
        className={styles.iconButton}
        aria-label={dismissLabel}
        data-testid={dismissTestId}
        onClick={onDismiss}
      >
        <RiCloseLine size={18} />
      </button>
    </div>
    {children}
  </div>
);

/** In-progress upload: neutral card with the filename and an indeterminate progress bar. */
const UploadingCard: FC<{ name: string; sizeLabel: string | null; onCancel: () => void }> = ({
  name,
  sizeLabel,
  onCancel,
}) => (
  <FileCardShell
    name={name}
    meta={_notNil(sizeLabel) ? `${sizeLabel} · uploading…` : 'uploading…'}
    dismissLabel="Cancel upload"
    onDismiss={onCancel}
    cardTestId="upload-field__uploading"
    filenameTestId="upload-field__filename"
  >
    <div className={styles.progressTrack} role="progressbar" aria-label="Uploading">
      <div className={styles.progressBar} />
    </div>
  </FileCardShell>
);

/** Completed upload: green card confirming the stored file. */
const SuccessCard: FC<{ name: string; sizeLabel: string | null; onRemove: () => void }> = ({
  name,
  sizeLabel,
  onRemove,
}) => (
  <FileCardShell
    className={styles.fileCardSuccess}
    name={name}
    meta={
      <span className={styles.fileMetaSuccess}>
        <RiCheckboxCircleFill size={16} />
        Uploaded{_notNil(sizeLabel) ? ` · ${sizeLabel}` : ''}
      </span>
    }
    dismissLabel="Remove file"
    onDismiss={onRemove}
    filenameTestId="upload-field__filename"
    dismissTestId="upload-field__remove"
  />
);

/** Invalid file type/size: red card explaining why, shown above the still-usable dropzone. */
const RejectedCard: FC<{ name: string; message: string; onDismiss: () => void }> = ({ name, message, onDismiss }) => (
  <FileCardShell
    className={styles.fileCardRejected}
    rejected
    alert
    name={name}
    meta={
      <span className={styles.fileMetaRejected}>
        <RiErrorWarningFill size={16} />
        {message}
      </span>
    }
    dismissLabel="Dismiss"
    onDismiss={onDismiss}
    cardTestId="upload-field__error"
    dismissTestId="upload-field__dismiss"
  />
);

/** Failed upload: full-width red banner with a retry action (no dropzone below). */
const FailureBanner: FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className={styles.failureBanner} data-testid="upload-field__failure" role="alert">
    <RiErrorWarningFill size={20} className={styles.failureIcon} />
    <div className={styles.failureBody}>
      <span className={styles.failureTitle}>Couldn’t upload your file.</span>
      <span className={styles.failureText}>{message}</span>
    </div>
    <button type="button" className={styles.retryButton} data-testid="upload-field__retry" onClick={onRetry}>
      <RiRefreshLine size={16} />
      Retry
    </button>
  </div>
);

/**
 * Single-file upload field for VerticalStepForm. Validates the picked file
 * against the config's accept/size rules, uploads it immediately via the
 * injected uploadFile callback (presign + PUT), and stores the resolved value
 * (an opaque, consumer-defined string) as the form value. Renders empty /
 * uploading / uploaded / error states.
 */
const SingleUploadField: FC<UploadFieldProps> = ({ config, onValueChange }) => {
  const { name, label, showLabel, accept, maxSizeBytes, helpText, uploadFile, rules } = config;
  const { control } = useFormContext();
  const inputId = useId();

  const [isUploading, setIsUploading] = useState(false);
  const [picked, setPicked] = useState<PickedFile | null>(null);
  const [error, setError] = useState<FieldError | null>(null);

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    rules: {
      ...rules,
      // The stored value is the uploaded value (an opaque string) or null; express "required" as a
      // presence check rather than RHF's default (which treats null as valid).
      validate: rules?.required
        ? (fieldValue) =>
            _notNil(fieldValue) ? true : typeof rules.required === 'string' ? rules.required : 'Required'
        : rules?.validate,
    },
  });

  // Tracks the in-flight upload so a superseding pick (or unmount) can abort it,
  // preventing a stale upload from resolving over a newer one. lastFileRef keeps
  // the most recent valid file so the failure state can offer a retry.
  const uploadAbortRef = useRef<AbortController | null>(null);
  const lastFileRef = useRef<File | null>(null);
  useEffect(() => () => uploadAbortRef.current?.abort(), []);

  const handleFile = async (file: File): Promise<void> => {
    // Abort any in-flight upload up front: a new pick — valid OR invalid —
    // supersedes it, so a stale upload must never resolve over the new file.
    uploadAbortRef.current?.abort();
    setIsUploading(false);
    setPicked({ name: file.name, size: file.size });

    const invalid = validateFile(file, accept, maxSizeBytes);
    if (_notNil(invalid)) {
      setError({ kind: 'validation', message: invalid });
      onChange(null);
      // Un-complete the step: a prior successful upload may have completed it,
      // and the value is now cleared (mirrors clear() and the success path).
      onValueChange?.();
      return;
    }

    const controller = new AbortController();
    uploadAbortRef.current = controller;
    lastFileRef.current = file;

    setError(null);
    setIsUploading(true);

    let url: string;
    try {
      url = await uploadFile(file, controller.signal);
    } catch (uploadError) {
      // A superseded/unmounted/cancelled upload was aborted deliberately — ignore it.
      if (!controller.signal.aborted) {
        // uploadFile owns reporting its own failures (it knows the transport);
        // capture here only as a backstop for anything that slipped through
        // unreported (e.g. a programming error), so no failure is fully silent.
        if (!(uploadError instanceof InVivoError)) {
          ExceptionHandler.captureException(
            new InVivoError('Unexpected error during file upload', {
              cause: uploadError instanceof Error ? uploadError : new Error(String(uploadError)),
              slug: 'upload-field-unexpected',
              level: 'warning',
            })
          );
        }
        setError({ kind: 'upload', message: 'The upload didn’t finish. Please try again.' });
        onChange(null);
        setIsUploading(false);
      }
      return;
    }

    if (controller.signal.aborted) {
      return;
    }
    onChange(url);
    onValueChange?.();
    setIsUploading(false);
  };

  const clear = (): void => {
    uploadAbortRef.current?.abort();
    lastFileRef.current = null;
    setIsUploading(false);
    setPicked(null);
    setError(null);
    onChange(null);
    onValueChange?.();
  };

  const retry = (): void => {
    if (_notNil(lastFileRef.current)) {
      void handleFile(lastFileRef.current);
    }
  };

  const { isDraggingOver, dropzoneProps } = useFileDropzone({
    onFiles: (files) => {
      const file = files[0];
      if (_notNil(file)) {
        void handleFile(file);
      }
    },
  });

  const displayName = picked?.name ?? (typeof value === 'string' ? fileNameFromUrl(value) : '');
  const sizeLabel = _notNil(picked) ? formatBytes(picked.size) : null;

  const dropzone = (stacked: boolean): ReactNode => (
    <div
      className={`${styles.dropzone} ${isDraggingOver ? styles.dropzoneActive : ''} ${stacked ? styles.dropzoneStacked : ''}`}
      data-testid="upload-field__dropzone"
      {...dropzoneProps}
    >
      <RiUploadCloud2Line size={28} className={styles.icon} />
      <p className={styles.prompt}>
        Drag protocol file here or{' '}
        <label htmlFor={inputId} className={styles.browse} data-testid="upload-field__browse">
          Browse
        </label>
      </p>
      {_notNil(helpText) && <p className={styles.caption}>{helpText}</p>}
    </div>
  );

  // Uploading and success take priority (driven by isUploading / the RHF value);
  // otherwise the current error — if any — decides between the failure banner,
  // the rejected-file card (above a usable dropzone), or the empty dropzone.
  const renderState = (): ReactNode => {
    if (isUploading) {
      return <UploadingCard name={displayName} sizeLabel={sizeLabel} onCancel={clear} />;
    }
    if (_notNil(value)) {
      return <SuccessCard name={displayName} sizeLabel={sizeLabel} onRemove={clear} />;
    }
    switch (error?.kind) {
      case 'upload':
        // Banner offers Retry (same file); the dropzone below lets the user pick
        // a different file instead of being stuck on the failed one.
        return (
          <>
            <FailureBanner message={error.message} onRetry={retry} />
            {dropzone(true)}
          </>
        );
      case 'validation':
        return (
          <>
            <RejectedCard name={displayName} message={error.message} onDismiss={clear} />
            {dropzone(true)}
          </>
        );
      default:
        return dropzone(false);
    }
  };

  return (
    <div>
      {showLabel && (
        <label htmlFor={inputId} className="db mb1 f6 near-black basier-med">
          {label}
        </label>
      )}

      {renderState()}

      <input
        id={inputId}
        type="file"
        className={styles.hiddenInput}
        accept={accept}
        aria-label={label}
        data-testid="upload-field__input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (_notNil(file)) {
            void handleFile(file);
          }
          event.target.value = '';
        }}
      />
    </div>
  );
};

// --- Multi-file mode ---

type MultiEntryStatus = 'uploading' | 'success' | 'rejected' | 'failed';

interface MultiEntry {
  /** Stable local id so cards keep their identity across re-renders and concurrent uploads. */
  id: number;
  name: string;
  size: number | null;
  status: MultiEntryStatus;
  /** Present only when status is 'success' — the stored value (opaque string) contributed to the form value. */
  url?: string;
  /** User-facing reason for a 'rejected' (validation) or 'failed' (upload) entry. */
  message?: string;
  /** Retained for 'failed' entries so the card can offer a retry of the same file. */
  file?: File;
}

const isSuccess = (entry: MultiEntry): boolean => entry.status === 'success';

/**
 * Seeds success entries from a previously-stored array value so state survives a step collapse/reopen.
 * `fileNameFor` resolves the display name from each stored value (needed when values are opaque ids,
 * not URLs); it falls back to `fileNameFromUrl` when no resolver is supplied or it returns nothing.
 */
const seedEntries = (
  value: unknown,
  startId: number,
  fileNameFor?: (value: string) => string | null
): Array<MultiEntry> => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((stored): stored is string => typeof stored === 'string' && stored.length > 0)
    .map((stored, index) => ({
      id: startId + index,
      name: fileNameFor?.(stored) ?? fileNameFromUrl(stored),
      size: null,
      status: 'success' as const,
      url: stored,
    }));
};

/**
 * Multi-file upload field for VerticalStepForm. Accepts a bundle of files, uploads each
 * independently via the injected uploadFile callback, and stores an `Array<string>` of the
 * resolved values (opaque strings; successful uploads only) as the form value. Renders one card per file with
 * its own state — uploading / uploaded / rejected / failed(+retry) — above a dropzone that admits
 * more files until `maxFiles` is reached.
 */
const MultiUploadField: FC<UploadFieldProps> = ({ config, onValueChange }) => {
  const { name, label, showLabel, accept, maxSizeBytes, maxFiles, helpText, uploadFile, fileNameFor, rules } = config;
  const { control } = useFormContext();
  const inputId = useId();

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    rules: {
      ...rules,
      // The stored value is an array of uploaded values (opaque strings); express "required" as "at
      // least one successful upload" rather than RHF's default (a non-empty array object is always truthy).
      validate: rules?.required
        ? (fieldValue) =>
            Array.isArray(fieldValue) && fieldValue.length > 0
              ? true
              : typeof rules.required === 'string'
                ? rules.required
                : 'Required'
        : rules?.validate,
    },
  });

  // Seed success entries from any pre-existing array value (ids 0..n-1); the running id counter
  // then starts just past them. Reading `entries.length` for the ref's initial value is a plain
  // state read, not a ref read during render.
  const [entries, setEntries] = useState<Array<MultiEntry>>(() => seedEntries(value, 0, fileNameFor));
  const idRef = useRef(entries.length);
  // Mirror of entries for use inside async upload callbacks, where the closed-over `entries` would
  // be stale — every mutation goes through commit(), which keeps this ref and state in lockstep.
  const entriesRef = useRef(entries);
  // One AbortController per in-flight upload, keyed by entry id, so a removed/superseded file can
  // be cancelled without disturbing the others.
  const controllersRef = useRef<Map<number, AbortController>>(new Map());

  useEffect(
    () => () => {
      controllersRef.current.forEach((controller) => controller.abort());
    },
    []
  );

  const nextId = (): number => {
    const id = idRef.current;
    idRef.current += 1;
    return id;
  };

  // Single funnel for every entries mutation: updates state + ref together, republishes the form
  // value (success entries' stored values only), and un-completes the step. Seeding on mount deliberately bypasses
  // this so a reopened step keeps its completed status and the value it already holds.
  const commit = (updater: (prev: Array<MultiEntry>) => Array<MultiEntry>): void => {
    const next = updater(entriesRef.current);
    entriesRef.current = next;
    setEntries(next);
    // Publish only success entries that actually carry a value; a success entry always has one, but
    // reducing (rather than mapping to `?? ''`) drops any without instead of emitting an empty string.
    onChange(
      next.reduce<Array<string>>((acc, entry) => {
        if (isSuccess(entry) && _notNil(entry.url)) {
          acc.push(entry.url);
        }
        return acc;
      }, [])
    );
    onValueChange?.();
  };

  const patchEntry = (id: number, patch: Partial<MultiEntry>): void => {
    commit((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const uploadEntry = async (id: number, file: File): Promise<void> => {
    const controller = new AbortController();
    controllersRef.current.set(id, controller);
    let url: string;
    try {
      url = await uploadFile(file, controller.signal);
    } catch (uploadError) {
      controllersRef.current.delete(id);
      // A removed/unmounted upload was aborted deliberately — leave its (now-absent) entry alone.
      if (controller.signal.aborted) {
        return;
      }
      // uploadFile owns reporting its own failures; capture here only as a backstop for anything
      // unreported (e.g. a programming error) so no failure is fully silent.
      if (!(uploadError instanceof InVivoError)) {
        ExceptionHandler.captureException(
          new InVivoError('Unexpected error during file upload', {
            cause: uploadError instanceof Error ? uploadError : new Error(String(uploadError)),
            slug: 'upload-field-multi-unexpected',
            level: 'warning',
          })
        );
      }
      patchEntry(id, { status: 'failed', message: 'The upload didn’t finish. Please try again.', file });
      return;
    }
    controllersRef.current.delete(id);
    if (controller.signal.aborted) {
      return;
    }
    patchEntry(id, { status: 'success', url, file: undefined, message: undefined });
  };

  const addFiles = (files: Array<File>): void => {
    const remaining = _notNil(maxFiles) ? Math.max(0, maxFiles - entriesRef.current.length) : files.length;
    const accepted = files.slice(0, remaining);
    const queued: Array<{ id: number; file: File }> = [];
    commit((prev) => {
      const additions = accepted.map((file) => {
        const id = nextId();
        const invalid = validateFile(file, accept, maxSizeBytes);
        if (_notNil(invalid)) {
          return { id, name: file.name, size: file.size, status: 'rejected' as const, message: invalid };
        }
        queued.push({ id, file });
        return { id, name: file.name, size: file.size, status: 'uploading' as const, file };
      });
      return [...prev, ...additions];
    });
    queued.forEach(({ id, file }) => {
      void uploadEntry(id, file);
    });
  };

  const removeEntry = (id: number): void => {
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
    commit((prev) => prev.filter((entry) => entry.id !== id));
  };

  const retryEntry = (id: number): void => {
    const entry = entriesRef.current.find((candidate) => candidate.id === id);
    if (_notNil(entry) && _notNil(entry.file)) {
      patchEntry(id, { status: 'uploading', message: undefined });
      void uploadEntry(id, entry.file);
    }
  };

  const { isDraggingOver, dropzoneProps } = useFileDropzone({
    onFiles: (files) => addFiles(Array.from(files)),
  });

  const atCapacity = _notNil(maxFiles) && entries.length >= maxFiles;

  const renderEntry = (entry: MultiEntry, index: number): ReactNode => {
    const sizeLabel = _notNil(entry.size) ? formatBytes(entry.size) : null;
    switch (entry.status) {
      case 'uploading':
        return (
          <FileCardShell
            key={entry.id}
            name={entry.name}
            meta={_notNil(sizeLabel) ? `${sizeLabel} · uploading…` : 'uploading…'}
            dismissLabel="Cancel upload"
            onDismiss={() => removeEntry(entry.id)}
            cardTestId={`upload-field__uploading--${index}`}
            filenameTestId={`upload-field__filename--${index}`}
          >
            <div className={styles.progressTrack} role="progressbar" aria-label="Uploading">
              <div className={styles.progressBar} />
            </div>
          </FileCardShell>
        );
      case 'success':
        return (
          <FileCardShell
            key={entry.id}
            className={styles.fileCardSuccess}
            name={entry.name}
            meta={
              <span className={styles.fileMetaSuccess}>
                <RiCheckboxCircleFill size={16} />
                Uploaded{_notNil(sizeLabel) ? ` · ${sizeLabel}` : ''}
              </span>
            }
            dismissLabel="Remove file"
            onDismiss={() => removeEntry(entry.id)}
            cardTestId={`upload-field__file--${index}`}
            filenameTestId={`upload-field__filename--${index}`}
            dismissTestId={`upload-field__remove--${index}`}
          />
        );
      case 'rejected':
        return (
          <FileCardShell
            key={entry.id}
            className={styles.fileCardRejected}
            rejected
            alert
            name={entry.name}
            meta={
              <span className={styles.fileMetaRejected}>
                <RiErrorWarningFill size={16} />
                {entry.message}
              </span>
            }
            dismissLabel="Dismiss"
            onDismiss={() => removeEntry(entry.id)}
            cardTestId={`upload-field__error--${index}`}
            filenameTestId={`upload-field__filename--${index}`}
            dismissTestId={`upload-field__dismiss--${index}`}
          />
        );
      case 'failed':
        return (
          <FileCardShell
            key={entry.id}
            className={styles.fileCardRejected}
            rejected
            alert
            name={entry.name}
            meta={
              <span className={styles.fileMetaRejected}>
                <RiErrorWarningFill size={16} />
                {entry.message}
              </span>
            }
            dismissLabel="Remove file"
            onDismiss={() => removeEntry(entry.id)}
            cardTestId={`upload-field__failure--${index}`}
            filenameTestId={`upload-field__filename--${index}`}
            dismissTestId={`upload-field__dismiss--${index}`}
          >
            <button
              type="button"
              className={styles.retryButton}
              data-testid={`upload-field__retry--${index}`}
              onClick={() => retryEntry(entry.id)}
            >
              <RiRefreshLine size={16} />
              Retry
            </button>
          </FileCardShell>
        );
      default: {
        const _exhaustive: never = entry.status;
        return _exhaustive;
      }
    }
  };

  return (
    <div>
      {showLabel && (
        <label htmlFor={inputId} className="db mb1 f6 near-black basier-med">
          {label}
        </label>
      )}

      {entries.length > 0 && <div className={styles.fileList}>{entries.map(renderEntry)}</div>}

      {!atCapacity && (
        <div
          className={`${styles.dropzone} ${isDraggingOver ? styles.dropzoneActive : ''} ${entries.length > 0 ? styles.dropzoneStacked : ''}`}
          data-testid="upload-field__dropzone"
          {...dropzoneProps}
        >
          <RiUploadCloud2Line size={28} className={styles.icon} />
          <p className={styles.prompt}>
            Drag files here or{' '}
            <label htmlFor={inputId} className={styles.browse} data-testid="upload-field__browse">
              Browse
            </label>
          </p>
          {_notNil(helpText) && <p className={styles.caption}>{helpText}</p>}
        </div>
      )}

      <input
        id={inputId}
        type="file"
        multiple
        className={styles.hiddenInput}
        accept={accept}
        aria-label={label}
        data-testid="upload-field__input"
        onChange={(event) => {
          const files = event.target.files;
          if (_notNil(files) && files.length > 0) {
            addFiles(Array.from(files));
          }
          event.target.value = '';
        }}
      />
    </div>
  );
};

/**
 * Upload field for VerticalStepForm. Dispatches to the single-file or multi-file implementation
 * based on `config.multiple`; single-file is the default and remains the behaviour for existing
 * consumers (study-protocol parser, health check).
 */
const UploadField: FC<UploadFieldProps> = ({ config, onValueChange }) =>
  config.multiple === true ? (
    <MultiUploadField config={config} onValueChange={onValueChange} />
  ) : (
    <SingleUploadField config={config} onValueChange={onValueChange} />
  );

export default UploadField;
